import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IAuthRepository } from '../../../core/interfaces/auth-repository.interface';
import { LoginCredentials } from '../../../core/models/login-credentials.model';
import { AuthResponse, AuthToken } from '../../../core/models/auth-token.model';
import { AuthStatusResponse } from '../../../core/models/user.model';
import { BaseRepository } from './base.repository';
import envConfig from '../../../env-config';

@Injectable({ providedIn: 'root' })
export class AuthRepository extends BaseRepository implements IAuthRepository {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${envConfig.apiUrl ?? ''}/api/v1/auth`;
  private readonly bffUrl = envConfig.bffUrl ?? '';

  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, credentials);
  }

  loginWithSSO(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/sso`, { provider: 'UKG' });
  }

  refreshToken(token: string): Observable<AuthToken> {
    return this.http.post<AuthToken>(`${this.apiUrl}/refresh`, {
      refreshToken: token,
    });
  }

  /**
   * Redirect to BFF login page.
   * BFF will handle OAuth flow with Keycloak and redirect back to frontend.
   */
  redirectToBFFLogin(): void {
    const bffLoginUrl = `${this.bffUrl}/api/auth/login`;
    this.navigateTo(bffLoginUrl);
  }

  private navigateTo(url: string): void {
    window.location.href = url;
  }

  /**
   * Check authentication status and get current user.
   * Returns BFF auth status response with isAuthenticated flag.
   * BFF returns 200 OK with isAuthenticated=false when not logged in.
   */
  checkAuthStatus(): Observable<AuthStatusResponse> {
    return this.http.get<AuthStatusResponse>(`${this.bffUrl}/api/auth/status`, {
      context: this.authContext(),
    });
  }

  /**
   * Logout via BFF.
   * Clears session cookie on BFF side.
   */
  logoutBFF(): Observable<void> {
    return this.http.post<void>(
      `${this.bffUrl}/api/auth/logout`,
      {},
      {
        context: this.authContext(),
      }
    );
  }

  /**
   * External login via Salesforce SAML.
   * Sends SAML response to BFF for validation and session creation.
   * BFF extracts x-saml-response header and forwards to API for validation.
   */
  externalLogin(samlResponse: string): Observable<AuthStatusResponse> {
    return this.http.post<AuthStatusResponse>(
      `${this.bffUrl}/api/v1/login/external`,
      {},
      {
        withCredentials: true,
        headers: new HttpHeaders({
          'x-saml-response': samlResponse,
        }),
      }
    );
  }
}
